import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedPosizioneDichiarata, MedStatoDisponibilita } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard, formatDateTime } from '../common'
import { DataTable } from '../DataTable'

/**
 * CENSO MED: which devices are in use or free now, and where they are — so
 * that a free monitor or ultrasound can be found, and a recalled device
 * reached. From network signals only (`med_availability_service`): the CENSO
 * agents' conversations, the flows, the monitors' announcements, the
 * sightings. Never clinical content.
 */

export function etichettaDisponibilita(stato: MedStatoDisponibilita | string): string {
  const e: Record<string, string> = {
    in_use: t('In uso'),
    not_connected: t('Acceso, non collegato alla centrale'),
    free: t('Libero'),
    recent: t('Usato di recente'),
    away: t('Non visto da qualche ora'),
    not_seen: t('Non visto da più di un giorno'),
    unknown: t('Nessun segnale'),
  }
  return e[stato] ?? stato
}

function tono(stato: string): string {
  return stato === 'in_use' ? 'info' : stato === 'free' || stato === 'not_connected' ? 'ok'
    : stato === 'not_seen' ? 'warn' : 'muted'
}

export function BadgeDisponibilita({ stato }: { stato: string }) {
  return <span className={`badge badge--${tono(stato)}`}>{etichettaDisponibilita(stato)}</span>
}

/** The position the device declares, in one line: «UTIC · LET04 · letto 4». */
export function testoDichiarata(p: MedPosizioneDichiarata | null | undefined): string {
  if (!p) return ''
  return [p.unit, p.room, p.bed ? t('letto {b}', { b: p.bed }) : null].filter(Boolean).join(' · ')
}

export function PosizioneDichiarata({ p }: { p: MedPosizioneDichiarata | null | undefined }) {
  if (!p) return <span className="text-muted">—</span>
  return (
    <>
      {testoDichiarata(p)}
      <small className="text-muted"> · {p.source}{p.seen_at ? `, ${formatDateTime(p.seen_at)}` : ''}</small>
    </>
  )
}

const ORDINE: MedStatoDisponibilita[] = ['in_use', 'not_connected', 'free', 'recent', 'away', 'not_seen', 'unknown']

export function VistaDisponibilita() {
  const dati = useAsync(() => api.medAvailability(), [])
  const d = dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d) return <ErrorBanner error={dati.error} />
  const s = d.thresholds
  return (
    <>
      <p className="field__hint">
        {t('Segnali di rete, non certezze cliniche: «in uso» = negli ultimi {m} minuti collegato a un sistema clinico (agente CENSO) o nelle ultime {h} ore traffico clinico nei flussi; «acceso, non collegato alla centrale» = un monitor Philips IntelliVue che si annuncia (lo fa solo senza centrale); «libero» = in rete ma senza traffico clinico da almeno {l} ore. La posizione «dichiarata» è quella impostata sul dispositivo dal personale, con l’ora in cui il dispositivo l’ha detta.', { m: s.in_use_agent_minutes, h: s.in_use_flows_hours, l: s.free_after_hours })}
      </p>
      <div className="wazuh-kpi">
        {ORDINE.filter((k) => k !== 'unknown' || d.counts.unknown > 0).map((k) => (
          <StatCard key={k} label={etichettaDisponibilita(k)} value={d.counts[k] ?? 0} hint={t('dispositivi')} />
        ))}
      </div>
      <Card title={t('Per tipo di dispositivo')}>
        <DataTable
          id="medical-availability-types"
          rows={d.by_category}
          rowKey={(r) => r.category}
          initialSort={{ key: 'free', dir: 'desc' }}
          conteggio={{ nome: t('tipi') }}
          nomeEsportazione="elettromedicali-disponibilita-tipi"
          columns={[
            { key: 'category', header: t('Tipo'), value: (r) => r.category },
            ...ORDINE.filter((k) => k !== 'unknown').map((k) => ({
              key: k, header: etichettaDisponibilita(k), type: 'number' as const, align: 'right' as const,
              value: (r: (typeof d.by_category)[number]) => r[k] ?? 0,
            })),
          ]}
        />
      </Card>
      <Card title={t('Dispositivi')}>
        <DataTable
          id="medical-availability"
          rows={d.devices}
          rowKey={(r) => r.id}
          initialSort={{ key: 'state' }}
          conteggio={{ nome: t('dispositivi') }}
          nomeEsportazione="elettromedicali-disponibilita"
          columns={[
            { key: 'name', header: t('Nome'), value: (r) => r.name, render: (r) => <Link to={`/medical/devices/${r.id}`}>{r.name || '—'}</Link> },
            { key: 'category', header: t('Tipo'), filter: 'select', value: (r) => r.category },
            { key: 'model', header: t('Modello'), value: (r) => [r.vendor, r.model].filter(Boolean).join(' ') },
            { key: 'state', header: t('Disponibilità'), filter: 'select', value: (r) => etichettaDisponibilita(r.state),
              sortValue: (r) => ORDINE.indexOf(r.state), render: (r) => <BadgeDisponibilita stato={r.state} /> },
            { key: 'connected_to', header: t('Collegato a'), value: (r) => r.connected_to ?? '' },
            { key: 'declared', header: t('Posizione dichiarata dal dispositivo'), value: (r) => testoDichiarata(r.declared),
              render: (r) => <PosizioneDichiarata p={r.declared} /> },
            { key: 'org_unit', header: t('Reparto registrato'), filter: 'select', value: (r) => r.org_unit ?? '' },
            { key: 'room', header: t('Stanza e letto'), value: (r) => [r.room, r.bed].filter(Boolean).join(' · '), hidden: true },
            { key: 'network', header: t('Dove lo vede la rete'), hidden: true,
              value: (r) => [r.switch_name && r.port ? `${r.switch_name} ${r.port}` : '', r.vlan_name ?? ''].filter(Boolean).join(' · ') },
            { key: 'last_activity_at', header: t('Ultima attività clinica'), type: 'date', value: (r) => r.last_activity_at ?? '',
              render: (r) => formatDateTime(r.last_activity_at) },
            { key: 'last_seen_at', header: t('Ultima volta in rete'), type: 'date', value: (r) => r.last_seen_at ?? '',
              render: (r) => formatDateTime(r.last_seen_at), hidden: true },
            { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (r) => r.inventory_number ?? '', hidden: true },
          ]}
        />
      </Card>
    </>
  )
}
